// SEO-12 · /resources — the public resource library index.
//
// The article cards are real <Link> anchors carrying data-testid="resource-card-<slug>" and
// data-updated: scripts/prerender.mjs crawls THIS page to discover every /resources/<slug> route
// and to read each article's own updated date for the sitemap <lastmod> (the same
// discover-from-the-rendered-page pattern the module catalogue and buyer guides use, so prerender
// coverage can never drift from what actually ships).
//
// The MarketingSeo props below are LITERAL on purpose — MarketingSeo.registry.test.js byte-matches
// them against the "/resources" row in prerender ROUTE_META.
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CalendarDays, Clock, LifeBuoy } from "lucide-react";
import { MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import BrandWordmarkText from "../components/BrandWordmarkText";
import ButtonLink from "../components/marketing/ButtonLink";
import { CTA_PRIMARY_LABEL } from "../components/marketing/navigation";
import { Reveal } from "./landingparts/parts1";
import { init as initAnalytics } from "../lib/analytics";
import {
  RESOURCES_BASE_PATH,
  RESOURCE_ARTICLES,
  RESOURCE_AUTHOR,
  resourcePath,
  resourcesByTopic,
} from "./resources/resourceLibrary";

// NOTE: the MarketingSeo title/description below are written as LITERALS (the registry test
// parses them); INDEX_DESCRIPTION exists so the JSON-LD description cannot drift from the meta.
const INDEX_DESCRIPTION =
  "Practical guides for Indian school leaders — fee reconciliation, DPDP duties, choosing an ERP, migrating without alarming parents and board-set grading.";

export const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

function indexSchema() {
  const url = `${seoOrigin}${RESOURCES_BASE_PATH}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: "Vidyapeeth360 Resources",
        description: INDEX_DESCRIPTION,
        url,
        publisher: { "@type": "Organization", name: "Vidyapeeth360" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${seoOrigin}/` },
          { "@type": "ListItem", position: 2, name: "Resources", item: url },
        ],
      },
      {
        "@type": "ItemList",
        name: "Vidyapeeth360 Resources",
        itemListElement: RESOURCE_ARTICLES.map((article, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: article.title,
          url: `${seoOrigin}${resourcePath(article.slug)}`,
        })),
      },
    ],
  };
}

export default function ResourcesIndexPage() {
  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, []);

  return (
    <MarketingMotion>
      <div className="landing-page flex min-h-screen flex-col bg-canvas text-ink" data-testid="resources-index-page">
        <MarketingSeo
          title="School Operations Resources & Guides | Vidyapeeth360"
          description="Practical guides for Indian school leaders — fee reconciliation, DPDP duties, choosing an ERP, migrating without alarming parents and board-set grading."
          path="/resources"
          schema={indexSchema()}
        />
        <MarketingTopBar />

        <main id="main" className="flex-1">
          <section className="mx-auto max-w-6xl px-5 pb-10 pt-12 sm:pt-16" aria-labelledby="resources-title">
            <p className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[13px] font-semibold text-ink-2">
              <BookOpen className="h-4 w-4 text-primary" aria-hidden="true" /> Resources
            </p>
            <h1
              id="resources-title"
              className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-[1.12] tracking-tight text-ink sm:text-[40px]"
            >
              How Indian schools actually run the work.
            </h1>
            <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-ink-2">
              Working notes from building <BrandWordmarkText /> — fee reconciliation, data-protection
              duties, migration, software evaluation and grading configuration. Written for the person
              who has to make the decision, and grounded in what the product actually does. Where a
              rule or a regulation is involved we link the primary source rather than paraphrasing it.
            </p>
          </section>

          <section className="border-y border-line bg-surface" aria-labelledby="resources-list-heading">
            <div className="mx-auto max-w-6xl px-5 py-12 sm:py-14">
              <h2 id="resources-list-heading" className="sr-only">
                Published guides
              </h2>
              {/* Grouped by topic (RESOURCE_TOPICS order) — at twenty guides a single flat grid
                  stops being scannable. The card markup, its data-testid and its data-updated are
                  unchanged, so prerender discovery and the browser gate keep working exactly as
                  before; only the sectioning is new. */}
              <div data-testid="resources-list" className="space-y-12">
                {resourcesByTopic().map((group) => (
                  <Reveal as="section" v="up" amount={0.08} key={group.key} aria-labelledby={`resources-topic-${group.key}`}>
                    <h3
                      id={`resources-topic-${group.key}`}
                      data-testid={`resources-topic-${group.key}`}
                      className="font-heading text-[13px] font-bold uppercase tracking-[0.14em] text-accent"
                    >
                      {group.label}
                    </h3>
                    <ul className="mt-5 grid gap-5 md:grid-cols-2">
                      {group.articles.map((article) => (
                        <li key={article.slug} className="flex">
                          <Link
                            to={resourcePath(article.slug)}
                            data-testid={`resource-card-${article.slug}`}
                            data-updated={article.updated}
                            className="group flex w-full flex-col rounded-2xl border border-line bg-canvas p-6 transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                          >
                            <span className="font-heading text-[19px] font-bold leading-snug text-ink transition-colors group-hover:text-accent">
                              {article.title}
                            </span>
                            <span className="mt-2.5 flex-1 text-[14.5px] leading-[1.7] text-ink-2">
                              {article.standfirst}
                            </span>
                            <span className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-ink-subtle">
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                                {fmtDate(article.published)}
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                                {article.readingMinutes} min read
                              </span>
                              <span className="ml-auto inline-flex items-center gap-1 font-semibold text-accent">
                                Read
                                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                              </span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                ))}
              </div>
              <p className="mt-8 text-[13px] leading-relaxed text-ink-subtle" data-testid="resources-authorship">
                Published by the {RESOURCE_AUTHOR.name}. Every guide is reviewed against our public
                claims registry before it ships — no invented statistics, no customer counts, and no
                regulatory claim we have not sourced.
              </p>
            </div>
          </section>

          {/* Cross-discovery: the two libraries have different governance — the Help Center is
              platform-admin-owned CMS content, these guides are code-authored under the editorial
              process — so they stay separate systems. What they must not do is hide each other. */}
          <section className="mx-auto max-w-6xl px-5 pt-12 sm:pt-14" aria-labelledby="resources-help-heading">
            <div
              className="rounded-2xl border border-line bg-surface p-6 sm:p-7"
              data-testid="resources-help-crosslink"
            >
              <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.13em] text-accent">
                <LifeBuoy className="h-4 w-4" aria-hidden="true" /> Also from us
              </p>
              <h2
                id="resources-help-heading"
                className="mt-2 font-heading text-[21px] font-bold leading-snug tracking-tight text-ink sm:text-[24px]"
              >
                Help Center
              </h2>
              <p className="mt-2 max-w-2xl text-[14.5px] leading-[1.7] text-ink-2">
                These guides are briefings on running an Indian school. The Help Center is the other
                half: step-by-step how-to guides for using <BrandWordmarkText /> itself — setting up
                fee collection, importing your students, configuring grading, sending parent
                messages.
              </p>
              <Link
                to="/help"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition-colors hover:border-accent/50 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                data-testid="resources-help-link"
              >
                Browse the Help Center <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          </section>

          <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" aria-labelledby="resources-close-heading">
            <div className="grid gap-7 rounded-[28px] border border-line bg-surface p-7 sm:p-10 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div>
                <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Bring your own case</p>
                <h2
                  id="resources-close-heading"
                  className="mt-2 max-w-2xl font-heading text-[26px] font-bold leading-tight tracking-tight text-ink sm:text-[32px]"
                >
                  Reading about it is cheaper than a migration. Testing it is better still.
                </h2>
                <p className="mt-3 max-w-2xl text-[14px] leading-[1.7] text-ink-2">
                  Start a workspace and import one class, or bring your fee structure and last year&rsquo;s
                  report card to a walkthrough. Either way you are judging the product, not a deck.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <ButtonLink to="/register" size="lg" data-testid="resources-register">
                  {CTA_PRIMARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </ButtonLink>
                <ButtonLink to="/pricing" size="lg" variant="outline" data-testid="resources-pricing">
                  See pricing
                </ButtonLink>
              </div>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </MarketingMotion>
  );
}
